/**
 * Drawing Process
 * 
 * Provides a drawing view window that can be spawned like other views.
 * Works independently of the background drawing theme.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import {
  startStroke,
  continueStroke,
  endStroke,
  renderAllStrokes,
  type DrawingTool,
  type DrawingStroke
} from '../../services/drawing/index.js';
import { 
  designTokens, 
  createHeading,
  createButton,
  createContainer,
  createSelect
} from '../../core/design-system.js';
import { nanoid } from 'nanoid';

interface DrawingProcessState {
  tool: DrawingTool;
  color: string;
  width: number;
}

interface DrawingPoint {
  x: number;
  y: number;
  pressure: number;
  time: number;
}

export class DrawingProcess extends BaseProcess {
  kind: string = 'drawing';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'utility' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: DrawingProcessState = {
    tool: 'pen',
    color: '#1a1a1a',
    width: 5
  };

  private drawingView: DrawingView | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[DrawingProcess] Starting drawing process ${this.id}`);
    
    // Listen for drawing view spawn request
    bus.on('drawing.show', () => {
      this.showDrawingView(ctx);
    });
    
    // Auto-show view on start (like other layout processes)
    this.showDrawingView(ctx);
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  protected onStop(ctx: ProcessContext): void {
    log.debug(`[DrawingProcess] Stopping drawing process ${this.id}`);
    
    // Close view if open
    if (this.drawingView) {
      ctx.destroyView(this.drawingView.id);
      this.views.delete(this.drawingView.id);
      this.drawingView = null;
    }
  }

  private showDrawingView(ctx: ProcessContext): void {
    if (this.drawingView) {
      return; // Already showing
    }
    
    const view = new DrawingView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.drawingView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  setTool(tool: DrawingTool): void {
    this.state.tool = tool;
    if (this.drawingView) {
      this.drawingView.updateState(this.state);
    }
  }

  setColor(color: string): void {
    this.state.color = color;
    if (this.drawingView) {
      this.drawingView.updateState(this.state);
    }
  }

  setWidth(width: number): void {
    this.state.width = width;
    if (this.drawingView) {
      this.drawingView.updateState(this.state);
    }
  }

  clearCanvas(): void {
    if (this.drawingView) {
      this.drawingView.clearCanvas();
    }
  }

  getState(): DrawingProcessState {
    return { ...this.state };
  }
}

/**
 * Drawing View
 * 
 * Provides a canvas for drawing with controls (tool, color, width, clear).
 */
class DrawingView extends BaseViewHTML {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing: boolean = false;
  private currentStroke: DrawingStroke | null = null;
  private strokes: DrawingStroke[] = [];
  private lastPoint: DrawingPoint | null = null;

  constructor(
    processId: string,
    viewId: string,
    private state: DrawingProcessState,
    private process: DrawingProcess
  ) {
    super(processId, viewId, {
      sizes: [1, 3], // [1] = full+standard (default), [3] = full+double
      showDismissButton: true,
      resizable: true,
    });
  }

  protected getContentHTML(): string {
    const heading = createHeading(2, 'Drawing');
    
    const toolSelect = createSelect(
      [
        { value: 'pen', label: 'Pen' },
        { value: 'brush', label: 'Brush' },
        { value: 'eraser', label: 'Eraser' }
      ],
      this.state.tool,
      {
        className: 'drawing-tool-select',
        name: 'tool'
      }
    );
    
    const colorInput = `
      <input 
        type="color" 
        class="drawing-color-input" 
        value="${this.escapeHtml(this.state.color)}"
        style="
          width: 100%;
          padding: ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          cursor: pointer;
        "
      />
    `;
    
    const widthInput = `
      <input 
        type="range" 
        class="drawing-width-input" 
        min="1" 
        max="20" 
        value="${this.state.width}"
        style="
          width: 100%;
          cursor: pointer;
        "
      />
      <div class="drawing-width-display" style="
        text-align: center;
        color: ${designTokens.contentColorMuted};
        font-size: ${designTokens.fontSize.sm};
        margin-top: ${designTokens.padding.xs};
      ">Width: ${this.state.width}px</div>
    `;
    
    const clearButton = createButton('Clear Canvas', {
      className: 'drawing-clear-button',
      variant: 'secondary'
    });
    
    const canvasHTML = `
      <canvas 
        class="drawing-canvas" 
        style="
          width: 100%;
          height: 300px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          cursor: crosshair;
          touch-action: none;
        "
      ></canvas>
    `;
    
    return createContainer(
      heading +
      `<div style="margin-bottom: ${designTokens.padding.md};">
        <label style="
          display: block;
          margin-bottom: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.sm};
          font-weight: 500;
          color: ${designTokens.contentColor};
        ">Tool</label>
        ${toolSelect}
      </div>` +
      `<div style="margin-bottom: ${designTokens.padding.md};">
        <label style="
          display: block;
          margin-bottom: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.sm};
          font-weight: 500;
          color: ${designTokens.contentColor};
        ">Color</label>
        ${colorInput}
      </div>` +
      `<div style="margin-bottom: ${designTokens.padding.md};">
        <label style="
          display: block;
          margin-bottom: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.sm};
          font-weight: 500;
          color: ${designTokens.contentColor};
        ">Width</label>
        ${widthInput}
      </div>` +
      `<div style="margin-bottom: ${designTokens.padding.md};">
        ${canvasHTML}
      </div>` +
      `<div style="margin-top: ${designTokens.padding.md};">
        ${clearButton}
      </div>`,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Setup canvas
    this.canvas = this.element.querySelector('.drawing-canvas') as HTMLCanvasElement;
    if (this.canvas) {
      // Set canvas size
      const rect = this.canvas.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;
      this.ctx = this.canvas.getContext('2d');
      
      if (this.ctx) {
        this.ctx.scale(dpr, dpr);
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
      }
      
      // Setup drawing event handlers
      this.setupDrawingHandlers();
      
      // Redraw all strokes
      this.redrawAllStrokes();
    }
    
    // Setup tool select
    const toolSelect = this.element.querySelector('.drawing-tool-select') as HTMLSelectElement;
    if (toolSelect) {
      toolSelect.addEventListener('change', (e) => {
        const tool = (e.target as HTMLSelectElement).value as DrawingTool;
        this.process.setTool(tool);
      });
    }
    
    // Setup color input
    const colorInput = this.element.querySelector('.drawing-color-input') as HTMLInputElement;
    if (colorInput) {
      colorInput.addEventListener('change', (e) => {
        const color = (e.target as HTMLInputElement).value;
        this.process.setColor(color);
      });
    }
    
    // Setup width input
    const widthInput = this.element.querySelector('.drawing-width-input') as HTMLInputElement;
    if (widthInput) {
      widthInput.addEventListener('input', (e) => {
        const width = parseInt((e.target as HTMLInputElement).value, 10);
        this.process.setWidth(width);
        // Update display
        const widthDisplay = this.element?.querySelector('.drawing-width-display');
        if (widthDisplay) {
          widthDisplay.textContent = `Width: ${width}px`;
        }
      });
    }
    
    // Setup clear button
    const clearButton = this.element.querySelector('.drawing-clear-button') as HTMLButtonElement;
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        if (confirm('Clear all drawings? This cannot be undone.')) {
          this.clearCanvas();
        }
      });
    }
  }

  private setupDrawingHandlers(): void {
    if (!this.canvas || !this.ctx) return;
    
    const getCanvasPoint = (e: MouseEvent | TouchEvent): { x: number; y: number; pressure: number } => {
      const rect = this.canvas!.getBoundingClientRect();
      let clientX: number;
      let clientY: number;
      let pressure = 0.5;
      
      if (e instanceof MouseEvent) {
        clientX = e.clientX;
        clientY = e.clientY;
        pressure = (e as any).pressure || 0.5;
      } else {
        const touch = e.touches[0] || e.changedTouches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
        pressure = (touch as any).force || 0.5;
      }
      
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
        pressure: Math.max(0.1, Math.min(1, pressure))
      };
    };
    
    const handleStart = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!this.canvas || !this.ctx) return;
      
      const point = getCanvasPoint(e);
      this.isDrawing = true;
      this.lastPoint = { ...point, time: Date.now() };
      
      this.currentStroke = {
        id: nanoid(),
        tool: this.state.tool,
        color: this.state.color,
        width: this.state.width,
        points: [this.lastPoint]
      };
    };
    
    const handleMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!this.isDrawing || !this.canvas || !this.ctx || !this.currentStroke) return;
      
      const point = getCanvasPoint(e);
      const drawingPoint: DrawingPoint = { ...point, time: Date.now() };
      this.currentStroke.points.push(drawingPoint);
      
      // Draw line segment
      this.ctx.save();
      
      if (this.state.tool === 'eraser') {
        this.ctx.globalCompositeOperation = 'destination-out';
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
      } else {
        this.ctx.globalCompositeOperation = 'source-over';
        this.ctx.strokeStyle = this.state.color;
      }
      
      const adjustedWidth = this.state.width * (0.5 + point.pressure * 0.5);
      this.ctx.lineWidth = adjustedWidth;
      
      this.ctx.beginPath();
      if (this.lastPoint) {
        this.ctx.moveTo(this.lastPoint.x, this.lastPoint.y);
        this.ctx.lineTo(point.x, point.y);
      }
      this.ctx.stroke();
      this.ctx.restore();
      
      this.lastPoint = drawingPoint;
    };
    
    const handleEnd = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!this.isDrawing || !this.currentStroke) return;
      
      // Save stroke if it has at least 2 points
      if (this.currentStroke.points.length >= 2) {
        this.strokes.push({ ...this.currentStroke });
      }
      
      this.isDrawing = false;
      this.currentStroke = null;
      this.lastPoint = null;
    };
    
    // Mouse events
    this.canvas.addEventListener('mousedown', handleStart);
    this.canvas.addEventListener('mousemove', handleMove);
    this.canvas.addEventListener('mouseup', handleEnd);
    this.canvas.addEventListener('mouseleave', handleEnd);
    
    // Touch events
    this.canvas.addEventListener('touchstart', handleStart, { passive: false });
    this.canvas.addEventListener('touchmove', handleMove, { passive: false });
    this.canvas.addEventListener('touchend', handleEnd, { passive: false });
    this.canvas.addEventListener('touchcancel', handleEnd, { passive: false });
  }

  private redrawAllStrokes(): void {
    if (!this.canvas || !this.ctx) return;
    
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Redraw all strokes
    for (const stroke of this.strokes) {
      if (stroke.points.length < 2) continue;
      
      this.ctx.save();
      
      if (stroke.tool === 'eraser') {
        this.ctx.globalCompositeOperation = 'destination-out';
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
      } else {
        this.ctx.globalCompositeOperation = 'source-over';
        this.ctx.strokeStyle = stroke.color;
      }
      
      this.ctx.lineWidth = stroke.width;
      
      this.ctx.beginPath();
      for (let i = 0; i < stroke.points.length; i++) {
        const point = stroke.points[i];
        if (i === 0) {
          this.ctx.moveTo(point.x, point.y);
        } else {
          this.ctx.lineTo(point.x, point.y);
        }
      }
      this.ctx.stroke();
      this.ctx.restore();
    }
  }

  clearCanvas(): void {
    this.strokes = [];
    this.currentStroke = null;
    this.isDrawing = false;
    this.lastPoint = null;
    this.redrawAllStrokes();
  }

  updateState(newState: DrawingProcessState): void {
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('drawing', (id: string) => new DrawingProcess(id));
